import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useLanguage } from '../../hooks/useLanguage';
import { Product } from '../../types/api';
import { formatDate } from '../../utils/dateFormatter';

interface ProductCardProps {
  product: Product;
  onStatusChange: (productId: string, productName: string, currentStatus: 'available' | 'sold') => void;
  onDelete: (productId: string, productName: string) => void;
  isUpdatingStatus?: boolean;
  isDeleting?: boolean;
}

export function ProductCard({
  product,
  onStatusChange,
  onDelete,
  isUpdatingStatus = false,
  isDeleting = false,
}: ProductCardProps) {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();

  const category = typeof product.categoryId === 'object' ? product.categoryId : null;
  const categoryName = category
    ? currentLanguage === 'ar'
      ? category.name
      : category.nameEn
    : '';

  return (
    <div className="card-glass rounded-lg p-4 hover:border-brand-accent/50 transition-all duration-300">
      <div className="flex flex-col gap-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            <h3 className="text-lg font-heading font-semibold text-neutral-light mb-1">
              {product.name}
            </h3>
            {product.description && (
              <p className="text-sm text-neutral-medium font-body line-clamp-2 whitespace-pre-line">
                {product.description}
              </p>
            )}
          </div>
        </div>

        {/* Details Grid */}
        <div className="grid grid-cols-2 gap-3">
          {category && (
            <div>
              <span className="text-xs text-neutral-medium font-body block mb-1">
                {t('admin.products.table.category')}
              </span>
              <Link
                to={`/admin/categories/${category._id}/edit`}
                className="text-sm text-brand-accent hover:underline font-body"
              >
                {categoryName}
              </Link>
            </div>
          )}
          <div>
            <span className="text-xs text-neutral-medium font-body block mb-1">
              {t('admin.products.table.value')}
            </span>
            <div className="font-mono text-xs sm:text-sm text-neutral-light font-body break-words whitespace-pre-line">
              {product.value}
            </div>
          </div>
          <div>
            <span className="text-xs text-neutral-medium font-body block mb-1">
              {t('admin.products.table.status')}
            </span>
            <span
              className={`inline-block px-2 py-1 rounded-full text-xs font-body ${
                product.status === 'available'
                  ? 'bg-green-500/20 text-green-500'
                  : 'bg-neutral-dark text-neutral-medium'
              }`}
            >
              {product.status === 'available'
                ? t('admin.products.status.available')
                : t('admin.products.status.sold')}
            </span>
          </div>
        </div>

        {/* Date */}
        <div>
          <span className="text-xs text-neutral-medium font-body">
            {t('admin.products.table.created')}: {formatDate(product.createdAt, 'PP h:mm a', currentLanguage)}
          </span>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-neutral-dark">
          <Link
            to={`/admin/products/${product._id}/edit`}
            className="flex-1 px-3 py-2 bg-brand-accent/20 text-brand-accent rounded-lg hover:bg-brand-accent/30 transition-colors text-sm font-body text-center"
            title={t('admin.products.actions.edit')}
          >
            {t('admin.products.actions.edit')}
          </Link>
          <button
            onClick={() => onStatusChange(product._id, product.name, product.status)}
            disabled={isUpdatingStatus}
            className={`flex-1 px-3 py-2 rounded-lg transition-colors text-sm font-body ${
              product.status === 'available'
                ? 'bg-yellow-500/20 text-yellow-500 hover:bg-yellow-500/30'
                : 'bg-green-500/20 text-green-500 hover:bg-green-500/30'
            }`}
            title={
              product.status === 'available'
                ? t('admin.products.actions.markSold')
                : t('admin.products.actions.markAvailable')
            }
          >
            {product.status === 'available'
              ? t('admin.products.actions.markSold')
              : t('admin.products.actions.markAvailable')}
          </button>
          <button
            onClick={() => onDelete(product._id, product.name)}
            disabled={isDeleting}
            className="flex-1 px-3 py-2 bg-danger/20 text-danger rounded-lg hover:bg-danger/30 transition-colors text-sm font-body disabled:opacity-50"
            title={t('admin.products.actions.delete')}
          >
            {t('admin.products.actions.delete')}
          </button>
        </div>
      </div>
    </div>
  );
}
